import React, { useState, useMemo } from 'react';
import { Table, Tabs, Select, Modal, Button, Input, Space, Tag, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { ExclamationCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    GET_DEGRADE_CONFIG,
    SET_DEGRADE_LEVEL,
    UPDATE_DEGRADE_OPERATION_LIST,
    BLOCK_OPERATION,
    UNBLOCK_OPERATION,
    GetDegradeConfigResponse,
    SetDegradeLevelResponse,
    UpdateDegradeOperationListResponse,
    BlockOperationResponse,
    UnblockOperationResponse,
    DegradeListType,
} from './graphql/degradeQueries';

const LEVEL_OPTIONS = [
    { value: 0, label: 'P0 - 仅核心功能', color: 'red' },
    { value: 1, label: 'P1 - 系统主体可用', color: 'orange' },
    { value: 2, label: 'P2 - 日常功能可用', color: 'blue' },
    { value: 3, label: 'P3 - 全部放行', color: 'green' },
];

type TabKey = 'blocked' | 'p0' | 'p1' | 'p2';

const TAB_CONFIG: Record<TabKey, { listType?: DegradeListType; dataKey: 'blockedOperations' | 'p0Whitelist' | 'p1Whitelist' | 'p2Blacklist' }> = {
    blocked: { dataKey: 'blockedOperations' },
    p0: { listType: 'P0_WHITELIST', dataKey: 'p0Whitelist' },
    p1: { listType: 'P1_WHITELIST', dataKey: 'p1Whitelist' },
    p2: { listType: 'P2_BLACKLIST', dataKey: 'p2Blacklist' },
};

const DegradeManagementPage: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<TabKey>('blocked');
    const [searchText, setSearchText] = useState('');
    const [addInputValue, setAddInputValue] = useState('');

    const { data, loading, refetch } = useQuery<GetDegradeConfigResponse>(
        GET_DEGRADE_CONFIG,
        { fetchPolicy: 'network-only' }
    );

    const [setDegradeLevel, { loading: levelLoading }] = useMutation<SetDegradeLevelResponse>(SET_DEGRADE_LEVEL, {
        onCompleted: () => {
            message.success(t('degrade.messages.levelChanged'));
            refetch();
        },
        onError: (error) => message.error(error.message),
    });

    const [updateOperationList, { loading: listLoading }] = useMutation<UpdateDegradeOperationListResponse>(
        UPDATE_DEGRADE_OPERATION_LIST,
        {
            onCompleted: () => {
                message.success(t('degrade.messages.listUpdated'));
                refetch();
            },
            onError: (error) => message.error(error.message),
        }
    );

    const [blockOperation, { loading: blockLoading }] = useMutation<BlockOperationResponse>(BLOCK_OPERATION, {
        onCompleted: () => {
            message.success(t('degrade.messages.operationBlocked'));
            setAddInputValue('');
            refetch();
        },
        onError: (error) => message.error(error.message),
    });

    const [unblockOperation, { loading: unblockLoading }] = useMutation<UnblockOperationResponse>(UNBLOCK_OPERATION, {
        onCompleted: () => {
            message.success(t('degrade.messages.operationUnblocked'));
            refetch();
        },
        onError: (error) => message.error(error.message),
    });

    const config = data?.getDegradeConfig;
    const currentLevel = config?.level ?? 3;

    const handleLevelChange = (newLevel: number) => {
        if (newLevel <= 1) {
            Modal.confirm({
                title: t('degrade.confirm.dangerousLevel.title'),
                icon: <ExclamationCircleOutlined />,
                content: t('degrade.confirm.dangerousLevel.content', {
                    level: LEVEL_OPTIONS.find(o => o.value === newLevel)?.label,
                }),
                okText: t('degrade.confirm.dangerousLevel.ok'),
                okType: 'danger',
                cancelText: t('degrade.confirm.dangerousLevel.cancel'),
                onOk: () => setDegradeLevel({
                    variables: { level: newLevel, confirmDangerousOperation: true },
                }),
            });
        } else {
            setDegradeLevel({ variables: { level: newLevel } });
        }
    };

    const handleAddOperation = () => {
        const opName = addInputValue.trim();
        if (!opName) return;

        if (activeTab === 'blocked') {
            blockOperation({ variables: { operationName: opName } });
        } else {
            const tabConf = TAB_CONFIG[activeTab];
            const currentList = config?.[tabConf.dataKey] ?? [];
            if (currentList.map(s => s.toLowerCase()).includes(opName.toLowerCase())) {
                message.warning(t('degrade.messages.alreadyExists'));
                return;
            }
            updateOperationList({
                variables: {
                    type: tabConf.listType,
                    operations: [...currentList, opName],
                },
            }).then(() => setAddInputValue(''));
        }
    };

    const handleRemoveOperation = (opName: string) => {
        if (activeTab === 'blocked') {
            unblockOperation({ variables: { operationName: opName } });
        } else {
            const tabConf = TAB_CONFIG[activeTab];
            const currentList = config?.[tabConf.dataKey] ?? [];
            updateOperationList({
                variables: {
                    type: tabConf.listType,
                    operations: currentList.filter(op => op.toLowerCase() !== opName.toLowerCase()),
                },
            });
        }
    };

    const tabDataSource = useMemo(() => {
        const tabConf = TAB_CONFIG[activeTab];
        const list = config?.[tabConf.dataKey] ?? [];
        const lowerSearch = searchText.toLowerCase();
        return list
            .filter(op => !searchText || op.toLowerCase().includes(lowerSearch))
            .map(op => ({ key: op, operationName: op }));
    }, [config, activeTab, searchText]);

    const columns: TableColumnsType<{ key: string; operationName: string }> = [
        {
            title: t('degrade.table.operationName'),
            dataIndex: 'operationName',
            key: 'operationName',
        },
        {
            title: t('degrade.table.action'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    loading={listLoading || unblockLoading}
                    onClick={() => handleRemoveOperation(record.operationName)}
                >
                    {t('degrade.table.remove')}
                </Button>
            ),
        },
    ];

    const levelOption = LEVEL_OPTIONS.find(o => o.value === currentLevel);

    return (
        <div className="flex flex-col h-full">
            {/* 降级级别 */}
            <div className="mb-4 flex items-center gap-4">
                <span className="font-medium">{t('degrade.currentLevel')}:</span>
                <Tag color={levelOption?.color}>{levelOption?.label}</Tag>
                <Select
                    value={currentLevel}
                    onChange={handleLevelChange}
                    loading={levelLoading}
                    options={LEVEL_OPTIONS}
                    style={{ width: 220 }}
                />
            </div>

            {/* Operation 列表管理 */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key as TabKey);
                    setSearchText('');
                    setAddInputValue('');
                }}
                items={[
                    { key: 'blocked', label: t('degrade.tabs.blocked') },
                    { key: 'p0', label: t('degrade.tabs.p0Whitelist') },
                    { key: 'p1', label: t('degrade.tabs.p1Whitelist') },
                    { key: 'p2', label: t('degrade.tabs.p2Blacklist') },
                ]}
            />

            {/* 搜索 + 添加 */}
            <div className="mb-3 flex items-center gap-3">
                <Input
                    placeholder={t('degrade.search.placeholder') ?? ''}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 300 }}
                />
                <Space.Compact>
                    <Input
                        placeholder={t('degrade.add.placeholder') ?? ''}
                        value={addInputValue}
                        onChange={e => setAddInputValue(e.target.value)}
                        onPressEnter={handleAddOperation}
                        style={{ width: 300 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        loading={blockLoading || listLoading}
                        onClick={handleAddOperation}
                    >
                        {t('degrade.add.button')}
                    </Button>
                </Space.Compact>
            </div>

            {/* 表格 */}
            <Table
                dataSource={tabDataSource}
                columns={columns}
                loading={loading}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} items` }}
                size="small"
            />
        </div>
    );
};

export default DegradeManagementPage;
